/**
 * Modal — SSOT for all dialog surfaces.
 *
 * Every modal in the app goes through this primitive. Sizes come from
 * the `--modal-w-*` tokens (see index.css). Lightweight confirmation
 * prompts should compose `<ConfirmDialog>` (in this folder) which
 * delegates to Modal with size='xs' + danger tone affordances.
 *
 * Contract:
 *   - Header/body/footer slots share one padding scale — callers never
 *     override spacing.
 *   - Backdrop click + Escape close by default; opt out with
 *     `closeOnBackdrop={false}` for destructive/loading mid-flight.
 *   - Focus trap + restore — tab stays inside the panel, closing
 *     returns focus to whatever had it before open.
 *   - iOS-safe body-scroll lock (no rubber-band).
 *   - `tone="danger"` tints the header bar so destructive flows stand
 *     out visually.
 */

import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

/** Modal width scale — maps to `--modal-w-*` tokens in index.css.
 *  xs (360) confirm dialogs, sm (440) simple actions, md (560) forms,
 *  lg (720) workflows, xl (960) advanced config/tables, fullscreen for
 *  rare complex flows. */
export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
/** Where to position the modal panel on desktop (≥1024px).
 *  Mobile always renders as a centered fullscreen sheet regardless. */
export type ModalPlacement = 'center' | 'right';
/** Default tone renders the header chrome neutrally; 'danger' tints it
 *  red for destructive flows (delete confirms, role removals, etc.). */
export type ModalTone = 'default' | 'danger';

export type ModalProps = {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: ModalSize;
    tone?: ModalTone;
    /** Accessible label — required for screen readers. */
    ariaLabel?: string;
    /** If false, clicking the backdrop does not close the modal. Default true. */
    closeOnBackdrop?: boolean;
    /** If false, pressing Escape does not close the modal. Default true.
     *  Use during loading/destructive confirms. */
    closeOnEscape?: boolean;
    /** Desktop placement. Default 'center'. 'right' renders a
     *  right-aligned drawer at ≥1024px. Ignored on mobile. */
    placement?: ModalPlacement;
};

type SlotProps = { children: ReactNode; className?: string };

function Modal({
    open,
    onClose,
    children,
    size = 'md',
    tone = 'default',
    ariaLabel,
    closeOnBackdrop = true,
    closeOnEscape = true,
    placement = 'center',
}: ModalProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    // Escape + focus trap + iOS-safe body scroll lock.
    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const panel = panelRef.current;
        const focusable = panel
            ? Array.from(
                  panel.querySelectorAll<HTMLElement>(
                      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
                  ),
              )
            : [];
        focusable[0]?.focus();

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && closeOnEscape) {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab' && panel) {
                const current = Array.from(
                    panel.querySelectorAll<HTMLElement>(
                        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
                    ),
                );
                if (current.length === 0) return;
                const first = current[0];
                const last = current[current.length - 1];
                if (!first || !last) return;
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKey);

        /* iOS-safe body scroll lock — overflow:hidden alone doesn't
         * stop Safari's rubber-band, so we also pin the body to the
         * current scroll position and restore on unmount. */
        const scrollY = window.scrollY;
        const body = document.body;
        const prev = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
        };
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';

        return () => {
            document.removeEventListener('keydown', handleKey);
            body.style.overflow = prev.overflow;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.width = prev.width;
            window.scrollTo(0, scrollY);
            previouslyFocused.current?.focus();
        };
    }, [open, onClose, closeOnEscape]);

    if (!open) return null;

    const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div
            className={`ui-modal-overlay ui-modal-overlay--${placement}`}
            onClick={handleBackdrop}
        >
            <div
                ref={panelRef}
                className={`ui-modal ui-modal--${size} ui-modal--${placement} ui-modal--tone-${tone}`}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}

type HeaderProps = SlotProps & {
    onClose?: () => void;
    /** Optional short line beneath the title. Use sparingly — most
     *  modals carry their context in the body copy, not the header. */
    subtitle?: ReactNode;
};

Modal.Header = ({ children, className = '', onClose, subtitle }: HeaderProps) => (
    <div className={`ui-modal__header ${className}`.trim()}>
        <div className="ui-modal__title-group">
            <h2 className="ui-modal__title">{children}</h2>
            {subtitle && <p className="ui-modal__subtitle">{subtitle}</p>}
        </div>
        {onClose && (
            <button type="button" className="ui-modal__close" onClick={onClose} aria-label="Close">
                <X size={18} />
            </button>
        )}
    </div>
);

Modal.Body = ({ children, className = '' }: SlotProps) => (
    <div className={`ui-modal__body ${className}`.trim()}>{children}</div>
);

Modal.Footer = ({ children, className = '' }: SlotProps) => (
    <div className={`ui-modal__footer ${className}`.trim()}>{children}</div>
);

export default Modal;
